/**
 * 实时通道：同源 WebSocket /v1/realtime（Worker 校验 Origin 后补上认证头转发给 kp-api 的 FamilyHub）。
 * 每 30 秒发 ping；断开后指数退避重连（2 → 60 秒）。消息格式见 docs/API.md 2.11：
 *   changed { seq }、presence { online_device_ids }、instant_reminder { log }、device_revoked { device_id }、
 *   device_apps_changed { device_id }（平板的 App 列表或卸载状态变了）、pong
 */
import { refreshSession } from "./api";
import { fromApi } from "./json";
import type { ReminderLog } from "./models";
import { setOnlineDeviceIds, setSyncStatus } from "./store";

export type RealtimeMessage =
  | { type: "changed"; seq: number }
  | { type: "presence"; onlineDeviceIds: string[] }
  | { type: "instant_reminder"; log: ReminderLog }
  | { type: "device_revoked"; deviceId: string }
  | { type: "device_apps_changed"; deviceId: string };

type Handlers = { [K in RealtimeMessage["type"]]: (m: Extract<RealtimeMessage, { type: K }>) => void };

const PING_MS = 30_000;
const MIN_DELAY = 2_000;
const MAX_DELAY = 60_000;

const listeners: { [K in keyof Handlers]: Set<Handlers[K]> } = {
  changed: new Set(),
  presence: new Set(),
  instant_reminder: new Set(),
  device_revoked: new Set(),
  device_apps_changed: new Set(),
};

let ws: WebSocket | null = null;
let wanted = false;
let delay = MIN_DELAY;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** 订阅某类消息，返回取消订阅函数 */
export function onRealtime<K extends keyof Handlers>(type: K, fn: Handlers[K]): () => void {
  listeners[type].add(fn);
  return () => listeners[type].delete(fn);
}

function emit(m: RealtimeMessage): void {
  for (const fn of listeners[m.type] as Set<(m: RealtimeMessage) => void>) {
    try {
      fn(m);
    } catch (e) {
      console.error("[realtime] 处理消息出错", e);
    }
  }
}

/** 解析服务端消息（键名转 camelCase）；不认识的返回 null */
export function parseRealtime(text: string): RealtimeMessage | null {
  let raw: { type?: string; seq?: number; log?: ReminderLog; deviceId?: string; onlineDeviceIds?: string[] };
  try {
    raw = fromApi(JSON.parse(text));
  } catch {
    return null;
  }
  switch (raw.type) {
    case "changed":
      return { type: "changed", seq: raw.seq ?? 0 };
    case "presence":
      return { type: "presence", onlineDeviceIds: raw.onlineDeviceIds ?? [] };
    case "instant_reminder":
      return raw.log ? { type: "instant_reminder", log: raw.log } : null;
    case "device_revoked":
      return { type: "device_revoked", deviceId: raw.deviceId ?? "" };
    case "device_apps_changed":
      return { type: "device_apps_changed", deviceId: raw.deviceId ?? "" };
    default:
      return null;
  }
}

function realtimeUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/v1/realtime`;
}

function clearTimers(): void {
  if (pingTimer) clearInterval(pingTimer);
  if (retryTimer) clearTimeout(retryTimer);
  pingTimer = null;
  retryTimer = null;
}

function open(): void {
  clearTimers();
  if (!wanted) return;
  setSyncStatus({ realtime: "connecting" });
  let opened = false;
  const sock = new WebSocket(realtimeUrl());
  ws = sock;
  sock.onopen = () => {
    opened = true;
    delay = MIN_DELAY;
    setSyncStatus({ realtime: "open" });
    console.info("[realtime] 已连接");
    pingTimer = setInterval(() => {
      if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: "ping" }));
    }, PING_MS);
  };
  sock.onmessage = (e) => {
    if (typeof e.data !== "string") return;
    const m = parseRealtime(e.data);
    if (!m) return;
    if (m.type === "presence") setOnlineDeviceIds(m.onlineDeviceIds);
    emit(m);
  };
  sock.onclose = () => {
    if (ws !== sock) return;
    ws = null;
    clearTimers();
    setSyncStatus({ realtime: "closed" });
    if (!wanted) return;
    // 没连上：可能是 access token 过期（升级请求被 401），先按需刷新令牌（refreshSession 会先查是否快过期）
    const before = opened ? Promise.resolve() : refreshSession().then(() => undefined, () => undefined);
    const wait = delay;
    delay = Math.min(delay * 2, MAX_DELAY);
    retryTimer = setTimeout(() => void before.then(open), wait);
  };
}

/** 连接（已连接时不重复连接） */
export function connectRealtime(): void {
  wanted = true;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  delay = MIN_DELAY;
  open();
}

/** 回到前台、网络恢复时：没连着就立即重连（不等退避） */
export function ensureRealtime(): void {
  if (!wanted) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  delay = MIN_DELAY;
  open();
}

export function disconnectRealtime(): void {
  wanted = false;
  clearTimers();
  const sock = ws;
  ws = null;
  if (sock) {
    sock.onclose = null;
    sock.close(1000, "bye");
  }
  setSyncStatus({ realtime: "closed" });
}
